import React, { useMemo, useState } from "react";
import "./AddProduct.css";
import Header from "../Components/Header";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../config";

const MAX_IMAGES = 6;
const MAX_IMAGE_SIZE_KB = 1000;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_KB * 1024;

const DEFAULT_DETAILS = {
  length: "",
  washCare: "",
  sleeves: "",
  neckline: "",
  dressInfo: "",
  disclaimer: "",
  whatYouWillReceive: "",
  customDetails: [],
};

const DEFAULT_COLOR_OPTIONS = [
  { name: "Red", hex: "#D32F2F" },
  { name: "Maroon", hex: "#800000" },
  { name: "Pink", hex: "#EC407A" },
  { name: "Orange", hex: "#F57C00" },
  { name: "Yellow", hex: "#FBC02D" },
  { name: "Green", hex: "#388E3C" },
  { name: "Blue", hex: "#1976D2" },
  { name: "Navy", hex: "#1A237E" },
  { name: "Purple", hex: "#7B1FA2" },
  { name: "Black", hex: "#111111" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Beige", hex: "#D7CCC8" },
  { name: "Gold", hex: "#D4AF37" },
  { name: "Grey", hex: "#757575" },
];

const AddProduct = () => {
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
  const [customColor, setCustomColor] = useState("");
  const [customColorHex, setCustomColorHex] = useState("#A21803");
  const [colorOptions, setColorOptions] = useState(
    DEFAULT_COLOR_OPTIONS
  );

  const [formData, setFormData] = useState({
    name: "",
    description: "",

    categoryId: "",
    subcategory: "",

    originalPrice: "",
    discountPercentage: "",

    stock: "",

    sku: "",
    weight: "",
    material: "",
    size: "",

    sizes: [],
    colors: [],

    status: "",
    tag: "",

    details: {
      ...DEFAULT_DETAILS,
    },
  });

  /*
   * ---------------------------------------------------------
   * PRICE CALCULATION
   * ---------------------------------------------------------
   *
   * This is only for instant UI preview.
   *
   * Backend ALSO calculates FinalPrice, so the backend
   * remains the final source of truth.
   */

  const priceCalculation = useMemo(() => {
    const originalPrice = Number(formData.originalPrice || 0);

    const discountPercentage =
      formData.discountPercentage === ""
        ? 0
        : Number(formData.discountPercentage || 0);

    if (originalPrice <= 0) {
      return {
        discountAmount: 0,
        finalPrice: 0,
      };
    }

    if (
      discountPercentage <= 0 ||
      discountPercentage > 100
    ) {
      return {
        discountAmount: 0,
        finalPrice: originalPrice,
      };
    }

    const discountAmount =
      (originalPrice * discountPercentage) / 100;

    const finalPrice = originalPrice - discountAmount;

    return {
      discountAmount,
      finalPrice,
    };
  }, [
    formData.originalPrice,
    formData.discountPercentage,
  ]);

  const formatPrice = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  /*
   * ---------------------------------------------------------
   * BASIC FORM CHANGE
   * ---------------------------------------------------------
   */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /*
   * ---------------------------------------------------------
   * DETAIL CHANGE
   * ---------------------------------------------------------
   */

  const handleDetailChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        [name]: value,
      },
    }));
  };

  /*
   * ---------------------------------------------------------
   * CUSTOM DETAILS
   * ---------------------------------------------------------
   */

  const handleAddCustomDetail = () => {
    setFormData((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        customDetails: [
          ...(prev.details.customDetails || []),
          {
            label: "",
            value: "",
          },
        ],
      },
    }));
  };

  const handleCustomDetailChange = (
    index,
    field,
    value
  ) => {
    setFormData((prev) => {
      const customDetails = [
        ...(prev.details.customDetails || []),
      ];

      customDetails[index] = {
        ...customDetails[index],
        [field]: value,
      };

      return {
        ...prev,
        details: {
          ...prev.details,
          customDetails,
        },
      };
    });
  };

  const handleRemoveCustomDetail = (index) => {
    setFormData((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        customDetails: (
          prev.details.customDetails || []
        ).filter(
          (_, detailIndex) => detailIndex !== index
        ),
      },
    }));
  };

  /*
   * ---------------------------------------------------------
   * COLORS
   * ---------------------------------------------------------
   */

  const handleColorToggle = (colorName) => {
    setFormData((prev) => {
      const isSelected =
        prev.colors.includes(colorName);

      return {
        ...prev,

        colors: isSelected
          ? prev.colors.filter(
            (item) => item !== colorName
          )
          : [...prev.colors, colorName],
      };
    });
  };

  const handleAddColor = () => {
    const colorName = customColor.trim();

    if (!colorName) {
      return;
    }

    const existingOption = colorOptions.find(
      (item) =>
        item.name.toLowerCase() ===
        colorName.toLowerCase()
    );

    if (!existingOption) {
      setColorOptions((prev) => [
        ...prev,
        {
          name: colorName,
          hex: customColorHex,
        },
      ]);
    }

    setFormData((prev) => ({
      ...prev,

      colors: prev.colors.some(
        (item) =>
          item.toLowerCase() ===
          colorName.toLowerCase()
      )
        ? prev.colors
        : [...prev.colors, colorName],
    }));

    setCustomColor("");
  };

  /*
   * ---------------------------------------------------------
   * IMAGE UPLOAD
   * ---------------------------------------------------------
   */

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) {
      return;
    }

    const oversizedFiles = files.filter(
      (file) =>
        file.size > MAX_IMAGE_SIZE_BYTES
    );

    const validFiles = files.filter(
      (file) =>
        file.size <= MAX_IMAGE_SIZE_BYTES
    );

    if (oversizedFiles.length > 0) {
      alert(
        `Each image must be below ${MAX_IMAGE_SIZE_KB} KB. ${oversizedFiles.length} image(s) were skipped.`
      );
    }

    const remainingSlots =
      MAX_IMAGES - imageFiles.length;

    if (remainingSlots <= 0) {
      alert(
        `Maximum ${MAX_IMAGES} images allowed.`
      );

      e.target.value = "";
      return;
    }

    const selectedFiles = validFiles.slice(
      0,
      remainingSlots
    );

    if (validFiles.length > remainingSlots) {
      alert(
        `Maximum ${MAX_IMAGES} images allowed.`
      );
    }

    const previewUrls = selectedFiles.map(
      (file) =>
        URL.createObjectURL(file)
    );

    setImages((prev) =>
      [...prev, ...previewUrls].slice(
        0,
        MAX_IMAGES
      )
    );

    setImageFiles((prev) =>
      [...prev, ...selectedFiles].slice(
        0,
        MAX_IMAGES
      )
    );

    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => {
      const imageToRemove = prev[index];

      if (
        imageToRemove?.startsWith?.("blob:")
      ) {
        URL.revokeObjectURL(imageToRemove);
      }

      return prev.filter(
        (_, imageIndex) =>
          imageIndex !== index
      );
    });

    setImageFiles((prev) =>
      prev.filter(
        (_, imageIndex) =>
          imageIndex !== index
      )
    );
  };

  /*
   * ---------------------------------------------------------
   * UPLOAD IMAGE TO BACKEND
   * ---------------------------------------------------------
   */

  const uploadImage = async (file) => {
    const uploadFormData = new FormData();

    uploadFormData.append("file", file);

    const response = await axios.post(
      `${API_BASE}/Upload`,
      uploadFormData,
      {
        headers: {
          "Content-Type":
            "multipart/form-data",
        },
      }
    );

    return (
      response.data.imageUrl ||
      response.data.url
    );
  };

  /*
   * ---------------------------------------------------------
   * SUBMIT PRODUCT
   * ---------------------------------------------------------
   */

  const handleSubmit = async () => {
    try {
      /*
       * Required fields
       */
      if (
        !formData.name.trim() ||
        !formData.description.trim() ||
        !formData.categoryId ||
        !formData.originalPrice ||
        !formData.stock ||
        !formData.status
      ) {
        alert(
          "Please fill all required fields!"
        );

        return;
      }

      /*
       * Validate Original Price
       */
      const originalPrice = Number(
        formData.originalPrice
      );

      if (
        !Number.isFinite(originalPrice) ||
        originalPrice <= 0
      ) {
        alert(
          "Original price must be greater than 0."
        );

        return;
      }

      /*
       * Validate Discount
       */
      let discountPercentage = null;

      if (
        formData.discountPercentage !== ""
      ) {
        discountPercentage = Number(
          formData.discountPercentage
        );

        if (
          !Number.isFinite(
            discountPercentage
          ) ||
          discountPercentage < 0 ||
          discountPercentage > 100
        ) {
          alert(
            "Discount percentage must be between 0 and 100."
          );

          return;
        }
      }

      /*
       * Validate Stock
       */
      const stock = Number(
        formData.stock
      );

      if (
        !Number.isFinite(stock) ||
        stock < 0
      ) {
        alert(
          "Stock cannot be negative."
        );

        return;
      }

      setLoading(true);

      /*
       * -----------------------------------------------------
       * Upload Images
       * -----------------------------------------------------
       */

      let uploadedImages = [];

      if (imageFiles.length > 0) {
        uploadedImages =
          await Promise.all(
            imageFiles.map(
              async (file, index) => {
                const imageUrl =
                  await uploadImage(
                    file
                  );

                return {
                  imageUrl,
                  isMain:
                    index === 0,
                  imageType:
                    "Product",
                };
              }
            )
          );
      }

      /*
       * If no image selected
       */
      if (
        uploadedImages.length === 0
      ) {
        uploadedImages.push({
          imageUrl:
            "https://via.placeholder.com/500x500.png?text=No+Image",
          isMain: true,
          imageType: "Product",
        });
      }

      /*
       * -----------------------------------------------------
       * Prepare Details
       * -----------------------------------------------------
       */

      const details = {
        length:
          formData.details.length?.trim() ||
          null,

        washCare:
          formData.details.washCare?.trim() ||
          null,

        sleeves:
          formData.details.sleeves?.trim() ||
          null,

        neckline:
          formData.details.neckline?.trim() ||
          null,

        dressInfo:
          formData.details.dressInfo?.trim() ||
          null,

        disclaimer:
          formData.details.disclaimer?.trim() ||
          null,

        whatYouWillReceive:
          formData.details
            .whatYouWillReceive
            ?.trim() || null,

        customDetails: (
          formData.details
            .customDetails || []
        )
          .filter(
            (detail) =>
              detail.label?.trim() ||
              detail.value?.trim()
          )
          .map((detail) => ({
            label:
              detail.label?.trim() ||
              null,

            value:
              detail.value?.trim() ||
              null,
          })),
      };

      /*
       * -----------------------------------------------------
       * FINAL API PAYLOAD
       * -----------------------------------------------------
       *
       * IMPORTANT:
       *
       * We DO NOT send finalPrice.
       *
       * Backend calculates FinalPrice from:
       *
       * OriginalPrice
       * DiscountPercentage
       */

      const payload = {
        name: formData.name.trim(),

        description:
          formData.description.trim(),

        categoryId:
          Number(formData.categoryId),

        subCategory:
          formData.subcategory?.trim() ||
          null,

        originalPrice,

        discountPercentage,

        quantity: stock,

        sku:
          formData.sku?.trim() ||
          null,

        weight:
          Number(formData.weight || 0),

        material:
          formData.material?.trim() ||
          null,

        size:
          formData.size?.trim() ||
          null,

        sizes: formData.sizes,

        status:
          Number(formData.status),

        tag:
          formData.tag?.trim() ||
          null,

        images:
          uploadedImages,

        colors:
          formData.colors.map(
            (colorName) => ({
              colorName,

              colorImageUrl:
                uploadedImages[0]
                  ?.imageUrl || null,
            })
          ),

        details,
      };

      /*
       * -----------------------------------------------------
       * CREATE PRODUCT
       * -----------------------------------------------------
       */

      const response =
        await axios.post(
          `${API_BASE}/Products`,
          payload
        );

      /*
       * Backend returns:
       *
       * {
       *   message,
       *   productId,
       *   originalPrice,
       *   discountPercentage,
       *   finalPrice
       * }
       */

      const createdProduct =
        response?.data || {};

      console.log(
        "Product created:",
        createdProduct
      );

      alert(
        `Product created successfully!\n\nFinal Price: ${formatPrice(
          createdProduct.finalPrice ??
          priceCalculation.finalPrice
        )}`
      );

      navigate("/products");
    } catch (error) {
      console.error(
        "Create product error:",
        error
      );

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to create product";

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * JSX
   * ---------------------------------------------------------
   */

  return (
    <div className="add-product-container">
      <Header />

      <div className="form-wrapper">
        <h1>Add New Product</h1>

        <p>
          Add product details below
        </p>

        {/* =================================================
            PRODUCT IMAGES
        ================================================= */}

        <div className="product-card">
          <h3>Product Images</h3>

          <div className="image-row">
            {Array.from(
              { length: MAX_IMAGES },
              (_, i) => (
                <div
                  key={i}
                  className={`image-box ${images[i]
                      ? "has-image"
                      : ""
                    }`}
                >
                  {images[i] ? (
                    <>
                      <img
                        src={images[i]}
                        alt={`preview-${i + 1
                          }`}
                      />

                      <button
                        type="button"
                        className="image-remove-btn"
                        onClick={() =>
                          handleRemoveImage(
                            i
                          )
                        }
                        title="Remove image"
                        aria-label={`Remove image ${i + 1
                          }`}
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <span className="image-empty-number">
                      {i + 1}
                    </span>
                  )}
                </div>
              )
            )}
          </div>

          <label
            className={`upload-box ${imageFiles.length >=
                MAX_IMAGES
                ? "disabled"
                : ""
              }`}
          >
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={
                handleImageUpload
              }
              disabled={
                imageFiles.length >=
                MAX_IMAGES
              }
            />

            <div className="upload-inner">
              <p>
                {imageFiles.length >=
                  MAX_IMAGES
                  ? "Maximum 6 images selected"
                  : "Click to upload images"}
              </p>

              <span>
                Max 6 images • Each image
                below 1000 KB •{" "}
                {imageFiles.length}/6
                selected
              </span>
            </div>
          </label>
        </div>

        {/* =================================================
            BASIC INFORMATION
        ================================================= */}

        <div className="product-card">
          <h3>Basic Information</h3>

          <label>
            Product Name *
          </label>

          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Banarasi Silk Saree"
          />

          <label>
            Description *
          </label>

          <textarea
            name="description"
            value={
              formData.description
            }
            onChange={handleChange}
            placeholder="Product description..."
          />
        </div>

        {/* =================================================
            CATEGORY & PRICING
        ================================================= */}

        <div className="product-card">
          <h3>
            Category & Pricing
          </h3>

          <div className="grid-2">
            <div>
              <label>
                Category *
              </label>

              <select
                name="categoryId"
                value={
                  formData.categoryId
                }
                onChange={handleChange}
              >
                <option value="">
                  Select Category
                </option>

                <option value="1">
                  Sarees
                </option>

                <option value="2">
                  Dresses
                </option>

                <option value="3">
                  Kurti
                </option>

                <option value="4">
                  Lehenga
                </option>
              </select>
            </div>

            <div>
              <label>
                Price *
              </label>

              <input
                type="number"
                name="originalPrice"
                value={
                  formData.originalPrice
                }
                onChange={handleChange}
                placeholder="12500"
                min="0"
                step="0.01"
              />
            </div>

            {/* =================================================
                DISCOUNT PERCENTAGE
            ================================================= */}

            <div>
              <label>
                Discount Percentage
              </label>

              <div className="discount-input-wrapper">
                <input
                  type="number"
                  name="discountPercentage"
                  value={
                    formData.discountPercentage
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="e.g. 20"
                  min="0"
                  max="100"
                  step="0.01"
                />

                <span>
                  %
                </span>
              </div>
            </div>

            {/* =================================================
                FINAL PRICE
            ================================================= */}

            <div>
              <label>
                Final Price
              </label>

              <input
                type="text"
                value={formatPrice(
                  priceCalculation.finalPrice
                )}
                readOnly
                className="final-price-input"
              />
            </div>

            {/* =================================================
                DISCOUNT AMOUNT
            ================================================= */}

            {priceCalculation.discountAmount >
              0 && (
                <div>
                  <label>
                    Discount Amount
                  </label>

                  <input
                    type="text"
                    value={formatPrice(
                      priceCalculation.discountAmount
                    )}
                    readOnly
                    className="discount-amount-input"
                  />
                </div>
              )}

            <div>
              <label>
                Stock *
              </label>

              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="10"
                min="0"
              />
            </div>
          </div>

          {/* =================================================
              PRICE SUMMARY
          ================================================= */}

          {Number(
            formData.originalPrice
          ) > 0 && (
              <div className="price-summary">
                <div className="price-summary-row">
                  <span>
                    Original Price
                  </span>

                  <strong>
                    {formatPrice(
                      Number(
                        formData.originalPrice
                      )
                    )}
                  </strong>
                </div>

                <div className="price-summary-row">
                  <span>
                    Discount
                  </span>

                  <strong>
                    {formData.discountPercentage ===
                      ""
                      ? "0%"
                      : `${formData.discountPercentage}%`}
                  </strong>
                </div>

                {priceCalculation.discountAmount >
                  0 && (
                    <div className="price-summary-row">
                      <span>
                        You Save
                      </span>

                      <strong>
                        {formatPrice(
                          priceCalculation.discountAmount
                        )}
                      </strong>
                    </div>
                  )}

                <div className="price-summary-row final">
                  <span>
                    Final Price
                  </span>

                  <strong>
                    {formatPrice(
                      priceCalculation.finalPrice
                    )}
                  </strong>
                </div>
              </div>
            )}
        </div>

        {/* =================================================
            STOCK DETAILS
        ================================================= */}

        <div className="product-card">
          <h3>
            Stock Details
          </h3>

          <div className="grid-2">
            <div>
              <label>
                SKU
              </label>

              <input
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                placeholder="SKU"
              />
            </div>

            <div>
              <label>
                Fabric Type
              </label>

              <input
                name="material"
                value={
                  formData.material
                }
                onChange={handleChange}
                placeholder="e.g. Ikat / Silk / Cotton"
              />
            </div>

            {/* =================================================
                COLORS
            ================================================= */}

            <div className="color-field">
              <label>
                Colors
              </label>

              <button
                type="button"
                className={`color-dropdown-trigger ${colorDropdownOpen
                    ? "open"
                    : ""
                  }`}
                onClick={() =>
                  setColorDropdownOpen(
                    (prev) => !prev
                  )
                }
              >
                <span>
                  {formData.colors
                    .length > 0
                    ? `${formData.colors.length} color${formData.colors
                      .length >
                      1
                      ? "s"
                      : ""
                    } selected`
                    : "Select colors"}
                </span>

                <span className="color-dropdown-arrow">
                  ⌄
                </span>
              </button>

              {formData.colors
                .length > 0 && (
                  <div className="selected-color-pills">
                    {formData.colors.map(
                      (colorName) => {
                        const option =
                          colorOptions.find(
                            (item) =>
                              item.name.toLowerCase() ===
                              colorName.toLowerCase()
                          );

                        return (
                          <span
                            className="selected-color-pill"
                            key={
                              colorName
                            }
                          >
                            <span
                              className="color-swatch"
                              style={{
                                backgroundColor:
                                  option?.hex ||
                                  colorName,
                              }}
                            />

                            {colorName}
                          </span>
                        );
                      }
                    )}
                  </div>
                )}

              {colorDropdownOpen && (
                <div className="color-dropdown-menu">
                  <div className="color-checkbox-list">
                    {colorOptions.map(
                      (option) => (
                        <label
                          className="color-checkbox-item"
                          key={
                            option.name
                          }
                        >
                          <input
                            type="checkbox"
                            checked={formData.colors.includes(
                              option.name
                            )}
                            onChange={() =>
                              handleColorToggle(
                                option.name
                              )
                            }
                          />

                          <span
                            className="color-swatch"
                            style={{
                              backgroundColor:
                                option.hex,
                            }}
                          />

                          <span>
                            {option.name}
                          </span>
                        </label>
                      )
                    )}
                  </div>

                  <div className="add-color-row">
                    <input
                      className="custom-color-text"
                      value={
                        customColor
                      }
                      onChange={(e) =>
                        setCustomColor(
                          e.target.value
                        )
                      }
                      placeholder="Add color name"
                    />

                    <input
                      className="custom-color-picker"
                      type="color"
                      value={
                        customColorHex
                      }
                      onChange={(e) =>
                        setCustomColorHex(
                          e.target.value
                        )
                      }
                      aria-label="Choose custom color"
                    />

                    <button
                      type="button"
                      onClick={
                        handleAddColor
                      }
                    >
                      + Add Color
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* =================================================
                SIZES
            ================================================= */}

            <div className="size-field">
              <label>
                Sizes
              </label>

              <div className="size-options">
                {[
                  "XS",
                  "S",
                  "M",
                  "L",
                  "XL",
                  "XXL",
                ].map((size) => (
                  <label
                    key={size}
                    className="size-checkbox"
                  >
                    <input
                      type="checkbox"
                      checked={formData.sizes.includes(
                        size
                      )}
                      onChange={(e) => {
                        const checked =
                          e.target.checked;

                        setFormData(
                          (prev) => ({
                            ...prev,

                            sizes: checked
                              ? [
                                ...prev.sizes,
                                size,
                              ]
                              : prev.sizes.filter(
                                (s) =>
                                  s !==
                                  size
                              ),
                          })
                        );
                      }}
                    />

                    <span>
                      {size}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* =================================================
                STATUS
            ================================================= */}

            <div>
              <label>
                Status *
              </label>

              <select
                name="status"
                value={
                  formData.status
                }
                onChange={handleChange}
              >
                <option value="">
                  Status
                </option>

                <option value="1">
                  Active
                </option>

                <option value="2">
                  Out Of Stock
                </option>
              </select>
            </div>

            {/* =================================================
                TAG
            ================================================= */}

            <div>
              <label>
                Tag
              </label>

              <input
                name="tag"
                value={formData.tag}
                onChange={handleChange}
                placeholder="New / Bestseller / Trending"
              />
            </div>
          </div>
        </div>

        {/* =================================================
            DETAILS
        ================================================= */}

        <div className="product-card product-details-form-card">
          <div className="details-card-heading">
            <div>
              <h3>
                Details
              </h3>

              <p>
                Add the product
                information shown in
                the Details section on
                the product page.
              </p>
            </div>
          </div>

          <div className="details-grid">
            <div>
              <label>
                Length
              </label>

              <input
                name="length"
                value={
                  formData.details
                    .length
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. 0.80 m (80 cm)"
              />
            </div>

            <div>
              <label>
                Wash Care
              </label>

              <input
                name="washCare"
                value={
                  formData.details
                    .washCare
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. Dry Wash"
              />
            </div>

            <div>
              <label>
                Sleeves
              </label>

              <input
                name="sleeves"
                value={
                  formData.details
                    .sleeves
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. Sleeveless"
              />
            </div>

            <div>
              <label>
                Neckline
              </label>

              <input
                name="neckline"
                value={
                  formData.details
                    .neckline
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. U Neck"
              />
            </div>

            <div className="details-grid-full">
              <label>
                Dress / Model Info
              </label>

              <input
                name="dressInfo"
                value={
                  formData.details
                    .dressInfo
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. The model is wearing a S size dress"
              />
            </div>

            <div className="details-grid-full">
              <label>
                What You Will Receive
              </label>

              <input
                name="whatYouWillReceive"
                value={
                  formData.details
                    .whatYouWillReceive
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. 1 Dress"
              />
            </div>

            <div className="details-grid-full">
              <label>
                Disclaimer
              </label>

              <textarea
                name="disclaimer"
                value={
                  formData.details
                    .disclaimer
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. Pictures are clicked in daylight. Color may vary slightly due to screen brightness."
              />
            </div>
          </div>

          {/* =================================================
              CUSTOM DETAILS
          ================================================= */}

          {(
            formData.details
              .customDetails || []
          ).length > 0 && (
              <div className="custom-details-list">
                {(
                  formData.details
                    .customDetails || []
                ).map(
                  (detail, index) => (
                    <div
                      className="custom-detail-row"
                      key={`custom-detail-${index}`}
                    >
                      <div>
                        <label>
                          Detail Name
                        </label>

                        <input
                          value={
                            detail.label
                          }
                          onChange={(e) =>
                            handleCustomDetailChange(
                              index,
                              "label",
                              e.target
                                .value
                            )
                          }
                          placeholder="e.g. Occasion"
                        />
                      </div>

                      <div>
                        <label>
                          Detail Value
                        </label>

                        <input
                          value={
                            detail.value
                          }
                          onChange={(e) =>
                            handleCustomDetailChange(
                              index,
                              "value",
                              e.target
                                .value
                            )
                          }
                          placeholder="e.g. Wedding / Festive"
                        />
                      </div>

                      <button
                        type="button"
                        className="remove-custom-detail-btn"
                        onClick={() =>
                          handleRemoveCustomDetail(
                            index
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>
                  )
                )}
              </div>
            )}

          <button
            type="button"
            className="add-new-detail-btn"
            onClick={
              handleAddCustomDetail
            }
          >
            + Add New Detail
          </button>
        </div>

        {/* =================================================
            ACTIONS
        ================================================= */}

        <div className="form-actions">
          <button
            type="button"
            className="btn cancel"
            onClick={() =>
              navigate("/products")
            }
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading
              ? "Creating..."
              : "Add Product"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;